import APIService, { clearInMemoryToken } from "./APIService";
import { notifyAuthChanged } from "./authEvents";
import BrowserFingerprinting from "../utils/BrowserFingerprintingUtil";
import CSRFService from "./CSRFService";
import { API_BASE_URL } from "../../../config/apiConfig";

// ── Logout race-condition guards ───────────────────────────────────────────
//
// Production traces showed that on multi-pane post-login landings (e.g.
// /account/billing) several React components mount simultaneously and each
// will independently attempt to call logout when their own boot-time check
// (e.g. "no `uuid` cookie yet") races ahead of UserContext.loginUser()
// finishing. That fired 4+ /auth/logout requests within a few hundred ms
// *immediately after a successful login*, wiping the brand-new HttpOnly
// authToken cookie the server had just set. The user then saw a blank /
// "deactivated" UI even though their server session was perfectly valid.
//
// Two guards mitigate this client-side:
//   1. Single-flight: at most one /auth/logout is ever in-flight at a time;
//      concurrent callers share the same Promise.
//   2. Post-login suppression window: after a successful login we refuse to
//      send /auth/logout for LOGIN_LOGOUT_GRACE_MS. A real user-initiated
//      logout that close to login is vanishingly rare; a stale-tab race is
//      the overwhelmingly likely cause and must not destroy the new session.
let inFlightLogoutPromise = null;
const LOGIN_LOGOUT_GRACE_MS = 5000;

const getLogoutSuppressUntil = () => {
  try {
    const v = sessionStorage.getItem("logoutSuppressUntil");
    return v ? parseInt(v, 10) : 0;
  } catch (e) {
    return 0;
  }
};

export const markRecentLogin = () => {
  try {
    sessionStorage.setItem(
      "logoutSuppressUntil",
      String(Date.now() + LOGIN_LOGOUT_GRACE_MS),
    );
  } catch (e) {
    // sessionStorage unavailable (private mode + Safari quirks) — degrade silently
  }
};

const clearLogoutSuppression = () => {
  try {
    sessionStorage.removeItem("logoutSuppressUntil");
  } catch (e) {
    /* noop */
  }
};

const UserAuthAPI = {
  /**
   * User login
   * @param {Object} credentials - Login credentials
   * @param {string} credentials.email - User email
   * @param {string} credentials.password - User password
   * @returns {Promise} - Promise that resolves with login response data
   */
  login: async (credentials) => {
    try {
      const baseApiUrl = API_BASE_URL;

      // TODO: Re-enable browser fingerprint when CORS is properly configured
      // Generate browser fingerprint
      // const browserFingerprint =
      //   await BrowserFingerprinting.generateFingerprint();

      // TODO: Re-enable geolocation when CORS is properly configured
      // Get geolocation if available (optional)
      // const geolocation = await BrowserFingerprinting.getGeolocation();

      const headers = {
        "Content-Type": "application/json",
        // TODO: Re-enable browser fingerprint header when CORS is properly configured
        // "X-Browser-Fingerprint": browserFingerprint,
      };

      // TODO: Re-enable geolocation header when CORS is properly configured
      // Add geolocation header if available
      // if (geolocation) {
      //   headers["X-Geolocation"] = JSON.stringify(geolocation);
      // }

      const response = await fetch(`${baseApiUrl}/api/v1/public/auth/login`, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(credentials),
        credentials: "include", // Include cookies in request and response
      });

      if (!response.ok) {
        // Try to parse error response body for more specific error messages
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = null;
        }

        // Provide more specific error information based on status code
        if (response.status === 401) {
          throw new Error("Invalid credentials");
        } else if (response.status === 403) {
          // Check if this is an email verification error
          if (errorData && errorData.message) {
            const error = new Error(errorData.message);
            error.status = 403;
            error.errorType = errorData.error || "Forbidden";
            throw error;
          }
          throw new Error("Access forbidden");
        } else if (response.status === 404) {
          const errorMessage =
            errorData && errorData.message
              ? errorData.message
              : "User account not found";
          throw new Error(errorMessage);
        } else if (response.status === 500) {
          throw new Error("Internal Server Error");
        } else {
          throw new Error(`Login failed with status: ${response.status}`);
        }
      }

      const data = await response.json();

      // Open a short grace window during which any /auth/logout call is
      // suppressed. Defends against stale-tab / multi-component races that
      // would otherwise wipe the freshly-issued auth cookies.
      markRecentLogin();

      // Fetch fresh CSRF token after successful login
      console.log("UserAuthAPI: Login successful, fetching CSRF token...");
      await CSRFService.fetchCsrfToken();

      return data;
    } catch (error) {
      console.error("UserAuthAPI: Login error:", error);

      // Re-throw with additional context for network errors
      if (
        error.name === "TypeError" &&
        error.message.includes("Failed to fetch")
      ) {
        const networkError = new Error("Failed to fetch");
        networkError.name = "TypeError";
        networkError.originalError = error;
        throw networkError;
      }

      throw error;
    }
  },

  /**
   * User logout
   * @param {Object} [opts]
   * @param {boolean} [opts.force=false] - Bypass the post-login suppression window.
   *   Set to `true` only for explicit user-initiated logouts (e.g. clicking the
   *   "Sign Out" button in the Header). Automatic / defensive logouts triggered
   *   by mount-time guards must NOT pass force:true.
   * @param {string} [opts.reason] - Audit reason forwarded to the server log.
   * @returns {Promise} - Promise that resolves when logout is complete
   */
  logout: async (opts = {}) => {
    const { force = false, reason = "unspecified" } = opts;

    // ── Post-login suppression window ──
    // If we just logged in (within LOGIN_LOGOUT_GRACE_MS) and this is not an
    // explicit user-initiated logout, treat the call as a no-op. This prevents
    // mount-time races on the post-login landing page from destroying the
    // brand-new session cookies.
    const suppressUntil = getLogoutSuppressUntil();
    if (!force && suppressUntil && Date.now() < suppressUntil) {
      console.warn(
        `UserAuthAPI: logout() suppressed — within post-login grace window (${
          suppressUntil - Date.now()
        }ms remaining, reason="${reason}")`,
      );
      return { success: true, suppressed: true, reason };
    }

    // ── Single-flight ──
    // Coalesce concurrent logout calls so multiple components racing to log out
    // produce exactly one /auth/logout network request.
    if (inFlightLogoutPromise) {
      console.log(
        "UserAuthAPI: logout() already in flight — joining existing promise",
      );
      return inFlightLogoutPromise;
    }

    const performLogout = async () => {
      try {
        // Force takes us past the grace window in any subsequent calls too.
        clearLogoutSuppression();

        // Call the backend logout endpoint with reason for audit logging
        console.log(
          `UserAuthAPI: Attempting backend logout (reason="${reason}", force=${force})...`,
        );
        await APIService.post("/api/v1/public/auth/logout", { reason });
        console.log("UserAuthAPI: Backend logout successful");

        // Clear in-memory + sessionStorage JWT, plus legacy localStorage entry
        clearInMemoryToken();
        localStorage.removeItem("groupId");
        localStorage.removeItem("groupUserCount");
        localStorage.removeItem("roles");
        localStorage.removeItem("jwtExpiry");
        localStorage.removeItem("dashboardView");
        localStorage.removeItem("userName");
        localStorage.removeItem("alertLevel");
        localStorage.removeItem("app-theme");
        localStorage.removeItem("userVerified");
        localStorage.removeItem("verifiedUserUuid");
        localStorage.removeItem("ada-contrast");
        localStorage.removeItem("ada-high-contrast");
        localStorage.removeItem("analyticsEnabled");
        localStorage.removeItem("dashboardMode");
        localStorage.removeItem("dateFormat");
        localStorage.removeItem("emailMarketing");
        localStorage.removeItem("emailSecurityAlerts");
        localStorage.removeItem("emailTrainingReminders");
        localStorage.removeItem("emailWeeklyDigest");
        localStorage.removeItem("gettingStarted_explored");
        localStorage.removeItem("landingPage");
        localStorage.removeItem("language");
        localStorage.removeItem("loginNotificationsEnabled");
        localStorage.removeItem("profileVisibility");
        localStorage.removeItem("pushNotificationsEnabled");
        localStorage.removeItem("showViewSettings");
        localStorage.removeItem("theme");
        localStorage.removeItem("timeFormat");
        localStorage.removeItem("timezone");
        localStorage.removeItem("tryitout_fingerprint");

        console.log("UserAuthAPI: Logout successful");
        return { success: true };
      } catch (error) {
        console.error("UserAuthAPI: Logout error:", error);
        console.error("UserAuthAPI: Error details:", {
          message: error.message,
          status: error.status,
          statusText: error.statusText,
        });

        // Even if the backend call fails, clear in-memory + sessionStorage token
        clearInMemoryToken();
        localStorage.removeItem("groupId");
        localStorage.removeItem("groupUserCount");
        localStorage.removeItem("roles");
        localStorage.removeItem("jwtExpiry");
        localStorage.removeItem("dashboardView");
        localStorage.removeItem("userName");
        localStorage.removeItem("alertLevel");
        localStorage.removeItem("app-theme");
        localStorage.removeItem("userVerified");
        localStorage.removeItem("verifiedUserUuid");
        localStorage.removeItem("ada-contrast");
        localStorage.removeItem("ada-high-contrast");
        localStorage.removeItem("analyticsEnabled");
        localStorage.removeItem("dashboardMode");
        localStorage.removeItem("dateFormat");
        localStorage.removeItem("emailMarketing");
        localStorage.removeItem("emailSecurityAlerts");
        localStorage.removeItem("emailTrainingReminders");
        localStorage.removeItem("emailWeeklyDigest");
        localStorage.removeItem("gettingStarted_explored");
        localStorage.removeItem("landingPage");
        localStorage.removeItem("language");
        localStorage.removeItem("loginNotificationsEnabled");
        localStorage.removeItem("profileVisibility");
        localStorage.removeItem("pushNotificationsEnabled");
        localStorage.removeItem("showViewSettings");
        localStorage.removeItem("theme");
        localStorage.removeItem("timeFormat");
        localStorage.removeItem("timezone");
        localStorage.removeItem("tryitout_fingerprint");

        console.log(
          "UserAuthAPI: Local cleanup completed despite backend error",
        );

        // Don't throw the error - logout should always succeed locally
        return {
          success: true,
          warning: "Backend logout failed but local cleanup completed",
          error: error.message,
        };
      }
    };

    inFlightLogoutPromise = performLogout().finally(() => {
      inFlightLogoutPromise = null;
      notifyAuthChanged();
    });
    return inFlightLogoutPromise;
  },

  /**
   * Check session status using HttpOnly cookies
   * @returns {Promise} - Promise that resolves with session status
   */
  checkSessionStatus: async () => {
    try {
      const baseApiUrl = API_BASE_URL;

      const response = await fetch(`${baseApiUrl}/api/v1/base/session/status`, {
        method: "GET",
        credentials: "include", // Important: This sends HttpOnly cookies
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log("✅ UserAuthAPI: Session status check successful:", data);
        return {
          success: true,
          authenticated: data.authenticated,
          userUuid: data.userUuid,
          userEmail: data.userEmail,
          lastAccessedAt: data.lastAccessedAt,
        };
      } else if (response.status === 401) {
        console.log("🚨 UserAuthAPI: Session expired or invalid");
        return {
          success: true,
          authenticated: false,
          reason: "Session expired or invalid",
        };
      } else {
        console.error(
          "❌ UserAuthAPI: Session status check failed:",
          response.status,
        );
        throw new Error(`Session status check failed: ${response.status}`);
      }
    } catch (error) {
      console.error("❌ UserAuthAPI: Error checking session status:", error);
      throw error;
    }
  },
};

export default UserAuthAPI;
